
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { BellIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useView } from "@/contexts/view-context"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"

// Mock notifications data
const mockNotifications = [
  {
    id: "1",
    title: "New workout plan available",
    message: "Check out your new personalized workout plan!",
    date: "2023-05-01T09:00:00",
    read: false,
  },
  {
    id: "2",
    title: "Membership renewal",
    message: "Your membership will expire in 7 days. Renew now!",
    date: "2023-05-02T14:30:00",
    read: false,
  },
  {
    id: "3",
    title: "Gym holiday hours",
    message: "We will be operating with special hours during the upcoming holiday.",
    date: "2023-05-03T11:15:00",
    read: true,
  },
  {
    id: "4",
    title: "New diet plan recommendation",
    message: "Based on your goals, we've created a new diet plan for you.",
    date: "2023-05-04T16:45:00",
    read: true,
  },
  {
    id: "5",
    title: "Fitness challenge starting soon",
    message: "Join our 30-day fitness challenge starting next week!",
    date: "2023-05-05T08:20:00",
    read: true,
  },
]

export function UserNotifications() {
  const { t } = useTranslation()
  const { hasUnreadNotifications, markNotificationsAsRead } = useView()
  const [notifications, setNotifications] = useState(mockNotifications)
  const [open, setOpen] = useState(false)

  const unreadCount = notifications.filter((n) => !n.read).length

  // Update the local state for notifications
  const markAllAsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, read: true })))
    markNotificationsAsRead() // Use the context function instead of direct state setter
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <BellIcon className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 h-4 w-4 rounded-full bg-red-500 text-[10px] font-medium text-white flex items-center justify-center">
              {unreadCount}
            </span>
          )}
          <span className="sr-only">{t("userView.notifications.title")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-medium">{t("userView.notifications.title")}</h4>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-8" onClick={markAllAsRead}>
              {t("userView.notifications.markAllRead")}
            </Button>
          )}
        </div>
        <ScrollArea className="h-[300px]">
          {notifications.length > 0 ? (
            <div className="flex flex-col">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn("p-4 border-b last:border-0 transition-colors", !notification.read && "bg-muted/50")}
                >
                  <div className="flex justify-between items-start mb-1">
                    <h5 className="font-medium text-sm">{notification.title}</h5>
                    <span className="text-xs text-muted-foreground">{formatDate(notification.date)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{notification.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">{t("userView.notifications.noNotifications")}</p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}
